import { createContext, useContext, useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { fetchBMWData } from '../services/ioBroker'
import { fetchReportTimeline, fetchEnergyFlow, fetchFormulas, fetchYearReport } from '../services/report'
import { fetchWiring } from '../services/wiring'
import { fetchHardware } from '../services/hardware'
import { fetchMcpTools } from '../services/mcp'
import { fetchBatteryCalibration } from '../services/batteryCalibration'
import { fetchBatteryTodayStats, fetchTodayEnergyStats, fetchCarChargeSummary, fetchBatterySummary, fetchBatteryPrice, fetchConsumptionBreakdown, fetchConsumptionLive, fetchApplianceAdvice } from '../services/influxdb'
import { fetchMqttOverview } from '../services/mqtt'
import { fetchCarDeparture } from '../services/carDeparture'
import { useLiveData } from './LiveDataContext'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

const fetchCarForceAmpere = () => fetch(`${API_BASE}/balance/carForceAmpere`).then(r => r.json()).catch(() => 0)

const DataContext = createContext(null)

export function useData() {
  return useContext(DataContext)
}

export function DataProvider({ children }) {
  const { iobrokerDashboard, iobrokerOffline, iobrokerUpdatedAt } = useLiveData()

  // ── ioBroker main — SSE-pushed pre-baked dashboard ──
  const carForceAmpereQ = useQuery({ queryKey: ['carForceAmpere'], queryFn: fetchCarForceAmpere, refetchInterval: 30_000 })
  const iobroker = useMemo(() => {
    if (!iobrokerDashboard) return null
    const amp = carForceAmpereQ.data ?? 0
    if (amp === (iobrokerDashboard.charger?.carForceAmpere ?? 0)) return iobrokerDashboard
    return { ...iobrokerDashboard, charger: { ...iobrokerDashboard.charger, carForceAmpere: amp } }
  }, [iobrokerDashboard, carForceAmpereQ.data])
  const iobrokerLoading = iobroker === null && !iobrokerOffline
  const iobrokerError   = iobrokerOffline ? 'iobroker offline' : null
  const lastUpdated     = iobrokerUpdatedAt

  // ── Polled queries ────────────────────────────────────────────────────────────
  const mqttOverviewQ = useQuery({ queryKey: ['mqttOverview'],  queryFn: fetchMqttOverview,      refetchInterval: 60_000 })
  const batteryTodayQ = useQuery({ queryKey: ['batteryToday'],  queryFn: fetchBatteryTodayStats, refetchInterval: 60_000 })
  // isError-only refs for serviceHealth — React Query deduplicates, no extra fetches
  const todayEnergyQ  = useQuery({ queryKey: ['todayEnergy'],   queryFn: fetchTodayEnergyStats,  refetchInterval: 60_000 })
  const bmwQ          = useQuery({ queryKey: ['bmw'],           queryFn: fetchBMWData,           refetchInterval: 60_000 })
  // Car charge totals only move when a charge does — 15 min is plenty. VehicleContext reads the cache.
  useQuery({ queryKey: ['carChargeSummary'], queryFn: fetchCarChargeSummary, refetchInterval: 15 * 60_000 })
  // Departure plan is recomputed server-side every minute. VehicleContext reads the cache.
  useQuery({ queryKey: ['carDeparture'], queryFn: fetchCarDeparture, refetchInterval: 60_000 })
  // Battery totals are priced from 5-min slots of complete days; today moves slowly. 15 min.
  const batterySummaryQ = useQuery({ queryKey: ['batterySummary'], queryFn: fetchBatterySummary, refetchInterval: 15 * 60_000 })
  // Rolling battery price — moves with every 5-min slot the pack charges in; the server caches 5 min.
  const batteryPriceQ = useQuery({ queryKey: ['batteryPrice'], queryFn: fetchBatteryPrice, refetchInterval: 60_000 })
  // Calibration (full charge, or the day hold after an unfinished night) — battery card line.
  const batteryCalibrationQ = useQuery({ queryKey: ['batteryCalibration'], queryFn: fetchBatteryCalibration, refetchInterval: 60_000 })
  // Consumption breakdown (metered vs. unexplained) — the server caches 5 min.
  const consumptionQ = useQuery({ queryKey: ['consumptionBreakdown'], queryFn: fetchConsumptionBreakdown, refetchInterval: 5 * 60_000 })
  // When to run washer / dryer / dishwasher — PV forecast + tariff; the server caches 1–10 min.
  const applianceAdviceQ = useQuery({ queryKey: ['applianceAdvice'], queryFn: fetchApplianceAdvice, refetchInterval: 10 * 60_000 })
  // Live split every 10 s — only while a consumption card has its Live view open
  // (it switches this on and off), so no client polls it for nothing.
  const [consumptionLiveOn, setConsumptionLiveOn] = useState(false)
  const consumptionLiveQ = useQuery({
    queryKey: ['consumptionLive'], queryFn: fetchConsumptionLive,
    refetchInterval: 10_000, enabled: consumptionLiveOn,
  })
  // Report page energy flow — every 10 s, only while its tab is open.
  const [flowOn, setFlowOn] = useState(false)
  const flowQ = useQuery({
    queryKey: ['energyFlow'], queryFn: fetchEnergyFlow,
    refetchInterval: 10_000, enabled: flowOn,
  })
  // Report page year comparison — past years never change; fetched when the tab opens or
  // its selection changes, no polling. The previous result stays up while the next loads.
  const [yearsOn, setYearsOn] = useState(false)
  const [yearsParams, setYearsParams] = useState({ period: 'ytd' })
  const yearsQ = useQuery({
    queryKey: ['yearReport', yearsParams], queryFn: () => fetchYearReport(yearsParams),
    enabled: yearsOn, staleTime: 10 * 60_000, placeholderData: keepPreviousData,
  })
  // Report page formulas — computed on the server; fetched when the tab opens, no polling.
  const [formulasOn, setFormulasOn] = useState(false)
  const formulasQ = useQuery({ queryKey: ['formulas'], queryFn: fetchFormulas, enabled: formulasOn, staleTime: 0 })
  // Report page wiring plan — fetched when its tab opens and after a save, no polling
  // (the live watts come from the energy flow above, which the tab switches on too).
  const [wiringOn, setWiringOn] = useState(false)
  const wiringQ = useQuery({ queryKey: ['wiring'], queryFn: fetchWiring, enabled: wiringOn, staleTime: 30_000 })
  // Docs → Schaltpläne — fetched when the tab opens and after a save, no polling
  const [hardwareOn, setHardwareOn] = useState(false)
  const hardwareQ = useQuery({ queryKey: ['hardware'], queryFn: fetchHardware, enabled: hardwareOn, staleTime: 30_000 })
  // Docs → MCP Tools — the catalogue only changes with a deploy; fetched when the page opens
  const [mcpToolsOn, setMcpToolsOn] = useState(false)
  const mcpToolsQ = useQuery({ queryKey: ['mcpTools'], queryFn: fetchMcpTools, enabled: mcpToolsOn, staleTime: 10 * 60_000 })
  // Report page timeline — only while the page is open (it switches this on and off).
  const [reportOn, setReportOn] = useState(false)
  const reportQ = useQuery({
    queryKey: ['reportTimeline'], queryFn: () => fetchReportTimeline(),
    refetchInterval: 60_000, enabled: reportOn,
  })

  // ── Service health ────────────────────────────────────────────────────────────
  const serviceHealth = useMemo(() => ({
    iobroker: iobrokerError        ? 'error' : 'ok',
    influxdb: todayEnergyQ.isError ? 'error' : 'ok',
    bmw:      bmwQ.isError         ? 'error' : 'ok',
  }), [iobrokerError, todayEnergyQ.isError, bmwQ.isError])

  const value = useMemo(() => ({
    iobroker, iobrokerLoading, iobrokerError, lastUpdated,
    batteryTodayStats: batteryTodayQ.data ?? null,
    batterySummary:    batterySummaryQ.data ?? null,
    batteryPrice:      batteryPriceQ.data ?? null,
    batteryCalibration: batteryCalibrationQ.data ?? null,
    consumptionBreakdown: consumptionQ.data ?? null,
    consumptionLive:      consumptionLiveOn ? (consumptionLiveQ.data ?? null) : null,
    setConsumptionLiveOn,
    reportTimeline:      reportOn ? (reportQ.data ?? null) : null,
    reportTimelineError: reportOn && reportQ.isError,
    setReportOn,
    energyFlow:      flowOn ? (flowQ.data ?? null) : null,
    energyFlowError: flowOn && flowQ.isError,
    setFlowOn,
    formulas:        formulasOn ? (formulasQ.data ?? null) : null,
    formulasError:   formulasOn && formulasQ.isError,
    formulasLoading: formulasOn && formulasQ.isFetching,
    reloadFormulas:  formulasQ.refetch,
    setFormulasOn,
    yearReport:        yearsOn ? (yearsQ.data ?? null) : null,
    yearReportError:   yearsOn && yearsQ.isError,
    yearReportLoading: yearsOn && yearsQ.isFetching,
    yearsParams, setYearsParams, setYearsOn,
    wiring:        wiringOn ? (wiringQ.data ?? null) : null,
    wiringError:   wiringOn && wiringQ.isError,
    reloadWiring:  wiringQ.refetch,
    setWiringOn,
    hardware:      hardwareOn ? (hardwareQ.data ?? null) : null,
    hardwareError: hardwareOn && hardwareQ.isError,
    setHardwareOn,
    mcpTools:      mcpToolsOn ? (mcpToolsQ.data ?? null) : null,
    mcpToolsError: mcpToolsOn && mcpToolsQ.isError,
    setMcpToolsOn,
    applianceAdvice:      applianceAdviceQ.data ?? null,
    mqttOverview:      mqttOverviewQ.data ?? null,
    serviceHealth,
  }), [
    iobroker, iobrokerLoading, iobrokerError, lastUpdated,
    batteryTodayQ.data, batterySummaryQ.data, batteryPriceQ.data, batteryCalibrationQ.data, consumptionQ.data, consumptionLiveOn, consumptionLiveQ.data, reportOn, reportQ.data, reportQ.isError, flowOn, flowQ.data, flowQ.isError, formulasOn, formulasQ.data, formulasQ.isError, formulasQ.isFetching, formulasQ.refetch, yearsOn, yearsQ.data, yearsQ.isError, yearsQ.isFetching, yearsParams, wiringOn, wiringQ.data, wiringQ.isError, wiringQ.refetch, hardwareOn, hardwareQ.data, hardwareQ.isError, mcpToolsOn, mcpToolsQ.data, mcpToolsQ.isError, applianceAdviceQ.data, mqttOverviewQ.data, serviceHealth,
  ])

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}
